'use client';

// IMPORTANT: Import global.css to ensure design system styles are available
// This is required because global-error renders outside the root layout
import './global.css';

import * as Sentry from '@sentry/nextjs';
import { Quicksand } from 'next/font/google';
import { NextIntlClientProvider, useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { ErrorContent } from '@/components/Error';
import { Button } from '@/components/ui/button';
// Import fallback translations for the error page
// This is necessary because global-error renders outside the root layout
import fallbackMessages from '@/i18n/locales/en/translation.json';

// Initialize the same font used in the root layout for consistency
const quickSand = Quicksand({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700'],
});

type GlobalErrorContentProps = {
  error: Error & { digest?: string };
  onReset: () => void;
};

function GlobalErrorContentWrapper({
  error,
  onReset,
}: GlobalErrorContentProps) {
  const translate = useTranslations();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <ErrorContent
      title={translate('globalErrorTitle')}
      description={error.message || translate('globalErrorDescription')}
      showHomeButton={true}
      actions={(
        <Button
          onClick={() => {
            onReset();
          }}
          variant="outline"
          startIcon="material-symbols:refresh"
        >
          {translate('tryAgain')}
        </Button>
      )}
    />
  );
}

/**
 * Global error boundary component.
 *
 * IMPORTANT: This component renders OUTSIDE the root layout, meaning it does not
 * have access to the NextIntlClientProvider from the root layout. We must provide
 * our own translation context here using fallback English translations.
 *
 * This component must include its own <html> and <body> tags as it completely
 * replaces the root layout when an error occurs. It must also import global.css
 * and set up fonts independently since the root layout styles are not available.
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error#global-error
 */
export default function GlobalError({
  error,
  reset: onReset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="en" className={quickSand.className}>
      <body>
        <NextIntlClientProvider locale="en" messages={fallbackMessages}>
          <GlobalErrorContentWrapper error={error} onReset={onReset} />
        </NextIntlClientProvider>
      </body>
    </html>
  );
}
